import { Injectable } from "@angular/core";
import { AssessmentPage, AssessmentQuestion, AssessmentSection } from "../models/assessments.interface";
import { AssessmentEditOpEnum, AssessmentQuestionTypeEnum } from "src/app/api/models/assessments/assessment.enum";
import { AssessmentAttachment, AssessmentCustomFieldDropdown, AssessmentDataFlow, AssessmentDropdownOption, AssessmentEditRequest, AssessmentQuestionMoveRequest, AssessmentQuestionRequest, AssessmentResourceId, AssessmentResourceValue, AssessmentSectionRequest } from "src/app/api/models/assessments/assessment.interface";
import { ChangesPayload, OrdinalityChangesPayload } from "../models/assessments-tracking-changes.interface";
import { CamelCaseWithSpacesPipe } from "src/app/shared/pipes/camel-case-with-spaces.pipe";
import { Risk } from "src/app/risks/models/risks.interface";
import { isCollaborationRoute } from "src/app/collaboration/collaborations.utils";
import { AssessmentsMapperService } from "./assessments-mapper.service";

@Injectable({
    providedIn: 'root'
})
export class AssessmentsTrackingChangesHelper {

    constructor(
        private assessmentsMapperService: AssessmentsMapperService,
    ) {}

    private camelCaseWithSpacesPipe = new CamelCaseWithSpacesPipe();

    getChangesPayload(assessment: AssessmentPage): ChangesPayload {
        try {
            const result = this.initializeChangesPayload();
            const activeSections = this.filterActiveSections(assessment.sections);
            
            this.trackAssessmentChanges(assessment, result);
            this.trackSectionChanges(activeSections, assessment, result);
            
            return result;
        } catch (error) {
            console.error('Error tracking changes:', error);
            return this.initializeChangesPayload();
        }
    }

    private initializeChangesPayload(): ChangesPayload {
        return {
            assessment: { updated: [] },
            section: { created: [], updated: [], deleted: [] },
            question: { created: [], updated: [], deleted: [] },
        };
    }

    private filterActiveSections(sections: AssessmentSection[]): AssessmentSection[] {
        return sections
            .filter(section => this.shouldProcessSection(section))
            .map(section => {
                const filteredSection = { ...section };
                if (filteredSection.questions) {
                    filteredSection.questions = filteredSection.questions.filter(question => this.shouldProcessQuestion(question));
                }
                return filteredSection;
            });
    }

    private shouldProcessSection(section: AssessmentSection): boolean {
        if (!section.isDeleted) return true;
        return Boolean(!section.isNew && section.id && section.originalState);
    }

    private shouldProcessQuestion(question: AssessmentQuestion): boolean {
        if (!question.isDeleted) return true;
        return Boolean(!question.isNew && question.id && question.originalState);
    }

    private trackAssessmentChanges(assessment: AssessmentPage, result: ChangesPayload): void {
        if (!this.shallowEqual(assessment.tags, assessment.originalState?.tags)) {
            result.assessment.updated.push({
                op: AssessmentEditOpEnum.Replace,
                path: '/tagIds',
                value: assessment.tags?.map(tag => tag.id) ?? []
            });
        }
        
        if (!this.shallowEqual(assessment.ownerId, assessment.originalState?.ownerId)) {
            result.assessment.updated.push({
                op: AssessmentEditOpEnum.Replace,
                path: '/ownerId',
                value: assessment.ownerId
            });
        }
    }

    private trackSectionChanges(sections: AssessmentSection[], assessment: AssessmentPage, result: ChangesPayload): void {
        for (const section of sections) {
            if(!isCollaborationRoute()) {
                if (section.isDeleted) {
                    this.handleSectionDeletion(section, result);
                    continue;
                }
                
                if (section.isNew) {
                    this.handleSectionCreation(section, assessment, result);
                    continue;
                }
                
                this.handleSectionUpdate(section, result);
            } 

            this.trackQuestionChanges(section, result);
        }
    }

    private handleSectionDeletion(section: AssessmentSection, result: ChangesPayload): void {
        if (!section.isNew && section.id && section.originalState) {
            result.section.deleted.push(section.id);
        }
    }

    private handleSectionCreation(section: AssessmentSection, assessment: AssessmentPage, result: ChangesPayload): void {
        const questionOrdinality: AssessmentResourceId[] = [];
        section.questions?.forEach(question => {
            if (!question.isDeleted && question.formControlId) {
                questionOrdinality.push(question.formControlId);
            }
        });

        result.section.created.push({
            assessmentId: assessment.id,
            formControlId: section.formControlId,
            label: section.label,
            name: section.name ?? this.camelCaseWithSpacesPipe.transform(section.label),
            questions: section.questions.map(question => this.mapQuestionToRequest(question)),
            layout: JSON.stringify({ questionOrdinality }),
            isHiddenFromCollaborators: !!section.isHiddenFromCollaborators,
            ownerAgentId: section.ownerAgentId ?? null,
        } as AssessmentSectionRequest);
    }

    private handleSectionUpdate(section: AssessmentSection, result: ChangesPayload): void {
        if (section.label !== section.originalState?.label) {
            result.section.updated.push({
                op: AssessmentEditOpEnum.Replace,
                path: '/label',
                value: section.label,
                sectionId: section.id
            });
        }

        // Section visibility (DevSectionVisibility): a visibility-only toggle changes no other field,
        // so without this diff the granular save emits no section edit and nothing reaches the server.
        if (!!section.isHiddenFromCollaborators !== !!section.originalState?.isHiddenFromCollaborators) {
            result.section.updated.push({
                op: AssessmentEditOpEnum.Replace,
                path: '/isHiddenFromCollaborators',
                value: !!section.isHiddenFromCollaborators,
                sectionId: section.id
            });
        }

        // Section owner (DevAssessmentSectionOwners): an owner-only change touches no other field, so
        // like the visibility toggle it needs its own diff or the granular save emits nothing for it.
        if ((section.ownerAgentId ?? null) !== (section.originalState?.ownerAgentId ?? null)) {
            result.section.updated.push({
                op: AssessmentEditOpEnum.Replace,
                path: '/ownerAgentId',
                value: section.ownerAgentId ?? null,
                sectionId: section.id
            });
        }
    }

    private trackQuestionChanges(section: AssessmentSection, result: ChangesPayload): void {
        for (const question of section.questions) {
            if (question.isDeleted) {
                this.handleQuestionDeletion(question, section, result);
                continue;
            }
            
            if (question.isNew) {
                this.handleQuestionCreation(question, section, result);
                continue;
            }
            
            this.handleQuestionUpdate(question, section, result);
        }
    }

    private handleQuestionDeletion(question: AssessmentQuestion, section: AssessmentSection, result: ChangesPayload): void {
        if (!question.isNew && question.id && question.originalState) {
            result.question.deleted.push(`${section.id}-${question.id}`);
        }
    }

    private handleQuestionCreation(question: AssessmentQuestion, section: AssessmentSection, result: ChangesPayload): void {
        result.question.created.push(this.mapQuestionToRequest(question, section.id));
    }

    private handleQuestionUpdate(question: AssessmentQuestion, section: AssessmentSection, result: ChangesPayload): void {
        // Check label changes
        if (question.label !== question.originalState?.label) {
            result.question.updated.push({
                op: AssessmentEditOpEnum.Replace,
                path: '/label',
                value: question.label,
                sectionId: section.id,
                questionId: question.id,
            });
        }
        
        // Check value changes with normalization
        this.checkQuestionValueChanges(question, section, result);

        // Check layout changes
        if (!this.shallowEqual(question.layout, question.originalState?.layout)) {
            // For question layouts, we need to preserve all fields (like description)
            // normalizeLayoutObject only handles ordinality and strips other fields
            let layoutValue: string | null = null;
            
            if (typeof question.layout === 'string') {
                // If it's already a string, validate it's valid JSON and use it
                // Skip if it's the string "null" (which is a bug)
                if (question.layout.trim() === 'null') {
                    return;
                }
                try {
                    JSON.parse(question.layout);
                    layoutValue = question.layout;
                } catch {
                    // Invalid JSON, skip this update
                    return;
                }
            } else if (question.layout && typeof question.layout === 'object') {
                // If it's an object, stringify it
                layoutValue = JSON.stringify(question.layout);
            } else if (question.layout === null || question.layout === undefined) {
                // If layout changed to null/undefined, we might want to send that
                // But only if originalState had a non-null layout (meaning it was cleared)
                if (question.originalState?.layout) {
                    // Layout was cleared, send null
                    layoutValue = null;
                } else {
                    // Both are null, shouldn't be here due to shallowEqual, but skip to be safe
                    return;
                }
            } else {
                // Unknown type, skip
                return;
            }
            
            // Only add update if we have a valid value (including null if layout was cleared)
            if (layoutValue !== undefined) {
                result.question.updated.push({
                    op: AssessmentEditOpEnum.Replace,
                    path: '/layout',
                    value: layoutValue === null ? null : layoutValue,
                    sectionId: section.id,
                    questionId: question.id,
                });
            }
        }
    }

    private checkQuestionValueChanges(question: AssessmentQuestion, section: AssessmentSection, result: ChangesPayload): void {
        const currentNormalized = this.normalizeValueByType(question.value, question.type);
        const originalNormalized = this.normalizeValueByType(question.originalState?.value, question.type);
        const valueChanged = !this.shallowEqual(currentNormalized, originalNormalized);
        const reasoningChanged = (question.reasoning ?? null) !== (question.originalState?.reasoning ?? null);

        if (!valueChanged && !reasoningChanged) {
            return;
        }

        // Persistence (evidence/reasoning) is always on for agents and the
        // collaborator surface. The pre-persistence format (a single op with
        // `value`/`/value` path and reasoning carried as a sibling field) is kept
        // as a fallback.
        if (!this.persistentReasoningEnabled()) {
            const op: AssessmentEditRequest = {
                op: AssessmentEditOpEnum.Replace,
                path: this.isPrimitive(question.value) ? 'value' : '/value',
                value: this.mapQuestionValue(question),
                sectionId: section.id,
                questionId: question.id,
            };
            if (reasoningChanged && question.reasoning) {
                op.reasoning = question.reasoning;
            }
            result.question.updated.push(op);
            return;
        }

        // BE contract: value and reasoning travel as two separate JSON-Patch ops
        // (`/value` + `/reasoning`). The `reasoning` carrier here is expanded into
        // its own op at the transport boundary (see buildQuestionPatchBody);
        // `null` clears the evidence. A reasoning-only change skips the `/value` op.
        if (valueChanged) {
            const op: AssessmentEditRequest = {
                op: AssessmentEditOpEnum.Replace,
                path: '/value',
                value: this.mapQuestionValue(question),
                sectionId: section.id,
                questionId: question.id,
            };
            if (reasoningChanged) {
                op.reasoning = question.reasoning ?? null;
            }
            result.question.updated.push(op);
        } else {
            result.question.updated.push({
                op: AssessmentEditOpEnum.Replace,
                path: '/reasoning',
                value: question.reasoning ?? null,
                sectionId: section.id,
                questionId: question.id,
            });
        }
    }

    private mapQuestionToRequest(question: AssessmentQuestion, sectionId?: AssessmentResourceId): AssessmentQuestionRequest {
        return {
            formControlId: question.formControlId,
            assessmentSectionId: sectionId,
            label: question.label,
            name: question.name ?? this.camelCaseWithSpacesPipe.transform(question.label),
            type: question.type,
            layout: question.layout,
            value: this.mapQuestionValue(question),
        } as AssessmentQuestionRequest;
    }

    getOrdinalityChangesPayload(assessment: AssessmentPage): OrdinalityChangesPayload {
        try {
            const result = this.initializeOrdinalityPayload();
            this.calculateOrdinality(assessment);
            if (!isCollaborationRoute()) {
                this.trackAssessmentOrdinalityChanges(assessment, result);
                this.trackSectionOrdinalityChanges(assessment, result);
            }
            return result;
        } 
        catch (error) {
            console.error('Error tracking ordinality changes:', error);
            return this.initializeOrdinalityPayload();
        }
    }

    private initializeOrdinalityPayload(): OrdinalityChangesPayload {
        return {
            assessment: { updated: [] },
            section: { updated: [] },
        };
    }

    /**
     * Detects questions that were re-parented to a different section since load, so the caller
     * can persist the move via the dedicated move endpoint BEFORE the section-ordinality PATCHes.
     * Original membership is read from each section's `originalState.questions` (the same snapshot
     * the ordinality diff uses); a question whose current parent section differs from that snapshot
     * moved. Scoped to existing questions moving into existing sections — a move into a brand-new
     * section is still carried by that section's create payload, and collaboration has no reorder.
     */
    getQuestionMoves(assessment: AssessmentPage): AssessmentQuestionMoveRequest[] {
        if (isCollaborationRoute()) {
            return [];
        }

        // questionId -> the section it was loaded under (original membership).
        const originalSectionByQuestionId = new Map<string, AssessmentResourceId>();
        for (const section of assessment.sections ?? []) {
            for (const question of section.originalState?.questions ?? []) {
                if (question?.id != null) {
                    originalSectionByQuestionId.set(String(question.id), section.id);
                }
            }
        }

        const moves: AssessmentQuestionMoveRequest[] = [];
        for (const section of assessment.sections ?? []) {
            if (section.isDeleted || section.isNew || section.id == null) {
                continue;
            }
            for (const question of section.questions ?? []) {
                if (question.isNew || question.isDeleted || question.id == null) {
                    continue;
                }
                const sourceSectionId = originalSectionByQuestionId.get(String(question.id));
                if (sourceSectionId != null && String(sourceSectionId) !== String(section.id)) {
                    moves.push({ questionId: question.id, sourceSectionId, targetSectionId: section.id });
                }
            }
        }
        return moves;
    }

    private calculateOrdinality(assessment: AssessmentPage): void {
        assessment.layout = this.getOrdinality(assessment.sections, 'section');
        for (const section of assessment.sections) {
            section.layout = this.getOrdinality(section.questions, 'question');
        }
    }

    private trackAssessmentOrdinalityChanges(assessment: AssessmentPage, result: OrdinalityChangesPayload): void {
        // Fallback: if original layout is missing, compute it from original sections order to avoid false positives
        const originalLayout = assessment.originalState?.layout ?? this.getOrdinality(assessment.originalState?.sections ?? [], 'section');
        if (!this.compareOrdinality(assessment.layout, originalLayout)) {
            result.assessment.updated.push({
                op: AssessmentEditOpEnum.Replace,
                path: '/layout',
                value: assessment.layout as string
            });
        }
    }
    private trackSectionOrdinalityChanges(assessment: AssessmentPage, result: OrdinalityChangesPayload): void {
        for (const section of assessment.sections) {
            if (section.isDeleted) continue;
            
            // Fallback: if original layout is missing, compute it from original questions order to avoid false positives
            const originalLayout = section.originalState?.layout ?? this.getOrdinality(section.originalState?.questions ?? [], 'question');
            if (!this.compareOrdinality(section.layout, originalLayout)) {
                result.section.updated.push({
                    op: AssessmentEditOpEnum.Replace,
                    path: '/layout',
                    value: section.layout as string,
                    sectionId: section.id
                });
            }
        }
    }

    getOrdinality<T extends { formControlId: AssessmentResourceId; isDeleted?: boolean }>(items: T[], type: 'section' | 'question'): string {
        // Simple array of unique formControlIds in order - index represents position.
        // Use a Set to guard against any accidental duplicates in the items collection.
        const ordinality: AssessmentResourceId[] = [];
        const seen = new Set<string>();

        for (const item of items ?? []) {
            const id = item?.formControlId;
            if (id && !item?.isDeleted) {
                const key = String(id);
                if (!seen.has(key)) {
                    seen.add(key);
                    ordinality.push(id);
                }
            }
        }

        const key = type === 'section' ? 'sectionOrdinality' : 'questionOrdinality';
        return JSON.stringify({ [key]: ordinality });
    }

    private shallowEqual<T>(a: T, b: T): boolean {
        if (!a && !b) return true;
        if (!a || !b) return false;
        
        if (a instanceof Map && b instanceof Map) {
            return this.compareMaps(a, b);
        }
        
        if (Array.isArray(a) && Array.isArray(b)) {
            return this.compareArrays(a, b);
        }
        
        if (typeof a === 'object' && typeof b === 'object') {
            return this.compareObjects(a, b);
        }
        
        return JSON.stringify(a) === JSON.stringify(b);
    }

    private compareMaps<K, V>(a: Map<K, V>, b: Map<K, V>): boolean {
        return a.size === b.size && 
               Array.from(a.entries()).every(([key, value]) => b.get(key) === value);
    }

    private compareArrays<T>(a: T[], b: T[]): boolean {
        return a.length === b.length && 
               a.every((item, index) => this.shallowEqual(item, b[index]));
    }

    private compareObjects<T>(a: T, b: T): boolean {
        const keysA = Object.keys(a as any);
        const keysB = Object.keys(b as any);
        
        if (keysA.length !== keysB.length) return false;
        
        return keysA.every(key => this.shallowEqual((a as any)[key], (b as any)[key]));
    }

    private compareOrdinality(layout: any, originalLayout: any): boolean {
        try {
            const normalizedLayout = this.assessmentsMapperService.normalizeLayoutObject(layout);
            const normalizedOriginalLayout = this.assessmentsMapperService.normalizeLayoutObject(originalLayout);
            if (!normalizedLayout || !normalizedOriginalLayout) return false;
            return this.shallowEqual(normalizedLayout, normalizedOriginalLayout);
        }
        catch (err) {
            console.error('Comparison failed:', err);
            return false;
        }
    }  

    /**
     * Whether the split `/value` + `/reasoning` PATCH (evidence persistence) is on.
     * Always on for agents and the collaborator surface.
     */
    private persistentReasoningEnabled(): boolean {
        return true;
    }

    private isPrimitive(val: unknown): boolean {
        return val === null || (typeof val !== 'object' && typeof val !== 'function');
    }

    private mapQuestionValue(question: AssessmentQuestion): AssessmentResourceValue {
        switch (question.type) {
            case AssessmentQuestionTypeEnum.Risks:
                return (question.value as Risk[])?.map(r => r.id);
            case AssessmentQuestionTypeEnum.LinkedAssessment:
            case AssessmentQuestionTypeEnum.BusinessPurpose:
                return (question.value as AssessmentPage[])?.map(a => a.id);
            case AssessmentQuestionTypeEnum.File:
                if (Array.isArray(question.value)) {
                    return (question.value as AssessmentAttachment[]).map(a => a.id);
                }
                return (question.value as AssessmentAttachment)?.id ?? null;
            case AssessmentQuestionTypeEnum.DropdownCustomField:
                 return this.mapCustomFieldDropdownValue(question.value as AssessmentCustomFieldDropdown);
            default:
                 return question.value;
        }
    }

    private mapCustomFieldDropdownValue(value: AssessmentCustomFieldDropdown): AssessmentCustomFieldDropdown {
        const { customFieldId, options } = value;
        if (!customFieldId) return null;
        if (!options) return {
            customFieldId,
            options: [],
        };

        return {
            customFieldId,
            options: options?.map(({ id, label, isSelected, layout }) => ({ 
                id, 
                label, 
                isSelected, 
                layout: this.normalizeLayoutValue(layout)
            })) ?? [],
        };
    }

    private normalizeValueByType(value: any, type: AssessmentQuestionTypeEnum): AssessmentResourceValue {
        if (!value) return value;
        
        switch (type) {
            case AssessmentQuestionTypeEnum.DropdownCustomField:
                return this.normalizeCustomFieldDropdown(value);
            case AssessmentQuestionTypeEnum.Dropdown:
                return this.normalizeOptionsArray(value ?? []) as unknown as AssessmentResourceValue;
            case AssessmentQuestionTypeEnum.Textarea:
            case AssessmentQuestionTypeEnum.Text:
            case AssessmentQuestionTypeEnum.ShortAnswer:
                return typeof value === 'string' ? this.normalizeHtmlString(value) : value;
            case AssessmentQuestionTypeEnum.Risks:
            case AssessmentQuestionTypeEnum.LinkedAssessment:
            case AssessmentQuestionTypeEnum.BusinessPurpose:
            case AssessmentQuestionTypeEnum.File:
                return this.normalizeIdArray(value);
            case AssessmentQuestionTypeEnum.DataFlow:
                return this.normalizeDataFlow(value);
            default:
                return Array.isArray(value) ? this.normalizeIdArray(value) : value;
        }
    }

    private normalizeCustomFieldDropdown(value: AssessmentCustomFieldDropdown): AssessmentCustomFieldDropdown {
        const { customFieldId, options } = value;
        return {
            customFieldId,
            options: this.normalizeOptionsArray(options)
        };
    }

    private normalizeHtmlString(input: string): string {
        try {
            let normalized = (input ?? '')
                .replace(/\r?\n/g, '')           // Remove newlines
                .replace(/>\s+</g, '><')         // Remove whitespace between tags
                .replace(/<p>\s*<br\s*\/>\s*<\/p>/gi, '') // Remove empty paragraphs with breaks
                .replace(/<p>\s*<\/p>/gi, '')    // Remove empty paragraphs
                .trim();                         // Trim outer whitespace
                
            // Try DOM normalization for consistent serialization
            try {
                const parser = new DOMParser();
                const doc = parser.parseFromString(normalized, 'text/html');
                normalized = (doc?.body?.innerHTML ?? normalized).trim();
            } catch {}
            
            return normalized;
        } catch {
            return (input ?? '').trim();
        }
    }

    private normalizeOptionsArray(options: AssessmentDropdownOption[] = []): AssessmentDropdownOption[] {
        return (options ?? [])
            .map(o => {
                let normalizedLayout: string | null = null;
                if (o.layout) {
                    normalizedLayout = this.normalizeLayoutValue(o.layout);
                }
                return { 
                    id: o.id, 
                    isSelected: !!o.isSelected, 
                    layout: normalizedLayout
                };
            })
            .sort((a, b) => String(a.id).localeCompare(String(b.id)));
    }

    private normalizeLayoutValue(layout: any): string | null {
        if (!layout) return null;
        
        // If layout is already a string, try to normalize it
        if (typeof layout === 'string') {
            try {
                // Try to parse it - if it's valid JSON, parse and re-stringify to normalize
                let parsed = JSON.parse(layout);
                // If the parsed value is still a string, it might be double-stringified
                // Try parsing again
                if (typeof parsed === 'string') {
                    try {
                        parsed = JSON.parse(parsed);
                    } catch {
                        // If second parse fails, use the first parsed value
                    }
                }
                // Re-stringify to get a consistent format
                return JSON.stringify(parsed);
            } catch {
                // If parsing fails, it's not valid JSON, use as-is
                return layout;
            }
        } else {
            // If it's an object, stringify it
            return JSON.stringify(layout);
        }
    }

    private normalizeDataFlow(value: any): AssessmentDataFlow {
        if (!value) return null;
        let { edges, serverLocations } = value;
        
        // Parse serverLocations if it's a string
        if (typeof serverLocations === 'string') {
            serverLocations = JSON.parse(serverLocations);
        }
        
        // Normalize edges - sort them consistently
        const normalizedEdges = edges ? this.normalizeDataFlowEdges(edges) : undefined;
        
        // Normalize serverLocations - sort systems and entities by id, and locations within each
        const normalizedServerLocations = serverLocations ? this.normalizeDataFlowLocations(serverLocations) : null;
        
        return {
            edges: normalizedEdges,
            serverLocations: normalizedServerLocations,
        };
    }

    private normalizeDataFlowEdges(edges: any[]): any[] {
        if (!Array.isArray(edges) || edges.length === 0) {
            return edges ?? [];
        }
        
        // Sort edges by source.entityId, then target.entityId, then nodeType
        return [...edges].sort((a, b) => {
            const sourceA = a.source?.entityId ?? '';
            const sourceB = b.source?.entityId ?? '';
            if (sourceA !== sourceB) {
                return sourceA.localeCompare(sourceB);
            }
            
            const targetA = a.target?.entityId ?? '';
            const targetB = b.target?.entityId ?? '';
            if (targetA !== targetB) {
                return targetA.localeCompare(targetB);
            }
            
            const sourceTypeA = a.source?.nodeType ?? '';
            const sourceTypeB = b.source?.nodeType ?? '';
            if (sourceTypeA !== sourceTypeB) {
                return sourceTypeA.localeCompare(sourceTypeB);
            }
            
            const targetTypeA = a.target?.nodeType ?? '';
            const targetTypeB = b.target?.nodeType ?? '';
            return targetTypeA.localeCompare(targetTypeB);
        });
    }

    private normalizeDataFlowLocations(serverLocations: any): any {
        if (!serverLocations) return null;
        
        const normalized: any = {};
        
        // Normalize systems array
        if (serverLocations.systems && Array.isArray(serverLocations.systems)) {
            normalized.systems = [...serverLocations.systems]
                .map(system => {
                    const normalizedSystem: any = { id: system.id };
                    const locationsArray = Array.isArray(system.locations) ? system.locations : [];
                    // Always include locations (possibly empty) to satisfy BE expectations
                    normalizedSystem.locations = [...locationsArray].sort((a, b) => a.localeCompare(b));
                    return normalizedSystem;
                })
                .sort((a, b) => (a.id ?? '').localeCompare(b.id ?? ''));
        }
        
        // Normalize entities array
        if (serverLocations.entities && Array.isArray(serverLocations.entities)) {
            normalized.entities = [...serverLocations.entities]
                .map(entity => {
                    const normalizedEntity: any = { id: entity.id };
                    const locationsArray = Array.isArray(entity.locations) ? entity.locations : [];
                    // Always include locations (possibly empty) to satisfy BE expectations
                    normalizedEntity.locations = [...locationsArray].sort((a, b) => a.localeCompare(b));
                    return normalizedEntity;
                })
                .sort((a, b) => (a.id ?? '').localeCompare(b.id ?? ''));
        }
        
        return normalized;
    }

    private normalizeIdArray(value: any): AssessmentResourceValue {
        if (!Array.isArray(value)) return value;
        if (value.length === 0) return [] as any;
        
        const first = value[0];
        
        if (this.isPrimitive(first)) {
            return this.normalizePrimitiveArray(value);
        }
        
        if (typeof first === 'object' && first && 'id' in first) {
            return this.normalizeObjectArray(value);
        }
        
        return value;
    }

    private normalizePrimitiveArray(value: any[]): AssessmentResourceValue {
        return [...value]
            .map(v => this.isPrimitive(v) ? String(v) : v)
            .sort((a, b) => String(a).localeCompare(String(b))) as any;
    }

    private normalizeObjectArray(value: any[]): AssessmentResourceValue {
        return value
            .map((o: any) => String(o.id))
            .sort((a: string, b: string) => a.localeCompare(b)) as any;
    }
}